import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import {
  TableCard,
  TableHeader,
  ClickableRow,
  EmptyState,
} from "./reports.styles";

interface ContactBreakdown {
  contactId: string;
  contactName: string;
  suburb: string;
  reportCount: number;
  lastSentAt: string | null;
}

interface ContactBreakdownTableProps {
  data?: ContactBreakdown[];
  loading?: boolean;
  onContactClick?: (contactId: string) => void;
  selectedContactId?: string | null;
}

export function ContactBreakdownTable({
  data,
  loading,
  onContactClick,
  selectedContactId,
}: ContactBreakdownTableProps) {
  const formatDate = (date: string | null) => {
    if (!date) return "Never";
    return new Date(date).toLocaleDateString("en-NZ");
  };

  if (loading) {
    return (
      <TableCard variant="outlined" sx={{ mb: 3 }}>
        <TableHeader>
          <Typography variant="subtitle1" fontWeight={600}>
            Per-Contact Breakdown
          </Typography>
        </TableHeader>
        <Box sx={{ p: 2 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={48} sx={{ mb: 1.5 }} />
          ))}
        </Box>
      </TableCard>
    );
  }

  if (!data || data.length === 0) {
    return (
      <TableCard variant="outlined" sx={{ mb: 3 }}>
        <EmptyState>
          <Typography variant="body2" color="text.secondary">
            No contact data available
          </Typography>
        </EmptyState>
      </TableCard>
    );
  }

  return (
    <TableCard variant="outlined" sx={{ mb: 3 }}>
      <TableHeader>
        <Typography variant="subtitle1" fontWeight={600}>
          Per-Contact Breakdown
        </Typography>
        {selectedContactId && (
          <Button
            onClick={() => onContactClick?.(selectedContactId)}
            color="primary"
            size="small"
          >
            Clear filter
          </Button>
        )}
      </TableHeader>
      <Box sx={{ overflowX: "auto" }}>
        <Table sx={{ minWidth: 500 }}>
          <TableHead>
            <TableRow>
              <TableCell>Contact</TableCell>
              <TableCell>Suburb</TableCell>
              <TableCell align="right">Reports</TableCell>
              <TableCell align="right">Last Sent</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((contact) => (
              <ClickableRow
                key={contact.contactId}
                selected={selectedContactId === contact.contactId}
                onClick={() => {
                  if (selectedContactId === contact.contactId) {
                    onContactClick?.("");
                  } else {
                    onContactClick?.(contact.contactId);
                  }
                }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {contact.contactName}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {contact.suburb}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Chip
                    label={contact.reportCount}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(contact.lastSentAt)}
                  </Typography>
                </TableCell>
              </ClickableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </TableCard>
  );
}
